import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MenuItem, TextField } from '@material-ui/core';
import MaterialUiPhoneNumber from 'material-ui-phone-number';
import Button from 'Components/Atoms/Button';
import IconButton from 'Components/Atoms/IconButton';
import { Close } from 'Components/Atoms/Icons';
import Typography from 'Components/Atoms/Typography';
import Modal from 'Components/Molecules/Modal';
import { useTranslation } from 'react-i18next';
import {
  createPaymentRequest,
  newRequestKey,
  PaymentRequest,
} from 'api/paymentRequestsApi';
import { Reservation } from 'types/reservations';
import LineEditor, { VatField } from './LineEditor';
import {
  defaultExpiryPreview,
  emptyForm,
  FormState,
  formToInput,
  previewTotalCents,
} from './paymentRequestForm';
import { calendarDate, dateTime, euro } from './format';
import { useFieldIds } from './fieldIds';

const Field: React.FC<{
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  type?: string;
  multiline?: boolean;
  helperText?: string;
  inputMode?: 'decimal' | 'numeric';
}> = ({
  id,
  label,
  value,
  onChange,
  error,
  disabled,
  type,
  multiline,
  helperText,
  inputMode,
}) => {
  const { t } = useTranslation('reservations');
  return (
    <TextField
      id={id}
      size="small"
      variant="outlined"
      fullWidth
      label={label}
      value={value}
      error={!!error}
      helperText={error ? t(error) : helperText}
      inputProps={{ inputMode }}
      disabled={disabled}
      type={type}
      multiline={multiline}
      rows={multiline ? 3 : undefined}
      InputLabelProps={{ shrink: true }}
      onChange={(event) => onChange(event.target.value)}
    />
  );
};

/**
 * Create a payment request at an existing reservation: purpose, gross amount
 * or positions, payer (guest or someone else), validity and channel; a
 * summary step shows what will be created before anything is sent.
 */
const CreatePaymentRequestModal: React.FC<{
  reservation: Reservation;
  onClose: () => void;
  onCreated: (request: PaymentRequest) => void;
}> = ({ reservation, onClose, onCreated }) => {
  const { t } = useTranslation('reservations');
  const fieldId = useFieldIds('payment-request');
  const [form, setForm] = useState<FormState>(emptyForm);
  const [clientRequestId] = useState(newRequestKey);
  const [step, setStep] = useState<'edit' | 'summary'>('edit');
  const formRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (step === 'summary') summaryRef.current?.focus();
  }, [step]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  // Two clicks can land before `busy` re-renders; the ref closes that gap.
  const submitting = useRef(false);
  const [error, setError] = useState('');
  const set = (patch: Partial<FormState>) => {
    setForm((current) => ({ ...current, ...patch }));
    const next = formToInput({ ...form, ...patch }, clientRequestId);
    const remaining = next.ok ? {} : next.errors;
    setErrors((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([key]) => key in remaining)
      )
    );
  };
  const result = useMemo(
    () => formToInput(form, clientRequestId),
    [form, clientRequestId]
  );
  const total = previewTotalCents(form);
  const guest = reservation.guest ?? ({} as any);
  const guestCountLabel =
    reservation.guests === 1
      ? t('1 person')
      : t('{{n}} persons', { n: reservation.guests });
  const payerName = form.payerSource === 'other' ? form.payerName : guest.name;
  const recipient =
    form.channel === 'email'
      ? form.payerSource === 'other'
        ? form.payerEmail
        : guest.email
      : form.channel === 'sms'
      ? form.payerSource === 'other'
        ? form.payerPhone
        : guest.phone
      : '';

  const review = () => {
    if (!result.ok) {
      setErrors(result.errors);
      requestAnimationFrame(() => {
        const field = formRef.current?.querySelector<HTMLElement>(
          '[aria-invalid="true"]'
        );
        field?.focus();
        field?.scrollIntoView({ block: 'center' });
      });
      return;
    }
    setErrors({});
    setStep('summary');
  };

  const submit = async () => {
    if (!result.ok || submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      onCreated(
        await createPaymentRequest(
          reservation.restaurant,
          reservation.id,
          result.input
        )
      );
    } catch (err: any) {
      setError(err.message);
      setBusy(false);
      submitting.current = false;
    }
  };

  return (
    <Modal
      open
      title={t('Create payment link')}
      maxWidth="md"
      fullWidth
      className="payment-request-modal"
      bodyProps={{ className: 'payment-request-modal__body' }}
      subHeader={
        <p className="payment-request-context">
          <strong>{guest.name}</strong>
          <span>
            {calendarDate(reservation.date)} · {reservation.time} ·{' '}
            {guestCountLabel}
          </span>
        </p>
      }
      role="dialog"
      aria-modal="true"
      aria-labelledby={fieldId('title')}
      titleProps={{ id: fieldId('title') }}
      titleTranslation={null}
      headerRight={
        <IconButton
          size="small"
          aria-label={t('Close')}
          disabled={busy}
          onClick={onClose}
          style={{ minWidth: 44, minHeight: 44, margin: -6 }}
        >
          <Close color="inherit" />
        </IconButton>
      }
      onClose={busy ? undefined : onClose}
      bottomPart={
        <div className="payment-request-footer">
          <div
            className="payment-request-total"
            aria-live="polite"
            aria-atomic="true"
          >
            <span>
              {t('Total')} <small>({t('incl. VAT')})</small>
            </span>
            <strong>{euro(total)}</strong>
          </div>
          <div className="payment-request-actions">
            <Button
              variant="outlined"
              translation={null}
              disabled={busy}
              onClick={step === 'edit' ? onClose : () => setStep('edit')}
            >
              {step === 'edit' ? t('Cancel creation') : t('Back')}
            </Button>
            <Button
              variant="primary"
              translation={null}
              loading={busy}
              disabled={busy}
              onClick={step === 'edit' ? review : submit}
            >
              {step === 'edit'
                ? t('Check summary')
                : form.channel === 'none'
                ? t('Create payment link')
                : t('Create and send')}
            </Button>
          </div>
        </div>
      }
    >
      {step === 'edit' ? (
        <div
          ref={formRef}
          className="payment-request-form payment-request-create"
        >
          <div className="payment-request-create__columns">
            <section
              className="payment-request-create__section"
              aria-labelledby={fieldId('delivery-heading')}
            >
              <h3 id={fieldId('delivery-heading')}>
                {t('Payer and delivery')}
              </h3>
              <TextField
                id={fieldId('payer')}
                select
                size="small"
                variant="outlined"
                fullWidth
                label={t('Payer')}
                value={form.payerSource}
                onChange={(event) =>
                  set({
                    payerSource: event.target.value as FormState['payerSource'],
                  })
                }
              >
                <MenuItem value="reservation">
                  {guest.name || t('Reservation guest')}
                </MenuItem>
                <MenuItem value="other">{t('Another person')}</MenuItem>
              </TextField>
              {form.payerSource === 'reservation' ? (
                <p className="payment-request-hint payment-request-contact">
                  {guest.email && <span>{guest.email}</span>}
                  {guest.phone && <span>{guest.phone}</span>}
                </p>
              ) : (
                <div className="payment-request-payer-fields">
                  <Field
                    id={fieldId('payer-name')}
                    label={t('Name')}
                    value={form.payerName}
                    error={errors.payerName}
                    onChange={(payerName) => set({ payerName })}
                  />
                  <Field
                    id={fieldId('payer-company')}
                    label={t('Company (optional)')}
                    value={form.payerCompany}
                    error={errors.payerCompany}
                    onChange={(payerCompany) => set({ payerCompany })}
                  />
                  <Field
                    type="email"
                    id={fieldId('payer-email')}
                    label={t('Email')}
                    value={form.payerEmail}
                    error={errors.payerEmail}
                    onChange={(payerEmail) => set({ payerEmail })}
                  />
                  <MaterialUiPhoneNumber
                    id={fieldId('payer-phone')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    defaultCountry="de"
                    preferredCountries={['de', 'at', 'ch']}
                    autoComplete="tel"
                    label={t('Mobile number')}
                    value={form.payerPhone}
                    error={!!errors.payerPhone}
                    helperText={
                      errors.payerPhone ? t(errors.payerPhone) : undefined
                    }
                    InputLabelProps={{ shrink: true }}
                    onChange={(value) =>
                      set({
                        payerPhone: String(value ?? '').replace(/[^+0-9]/g, ''),
                      })
                    }
                  />
                  <TextField
                    id={fieldId('language')}
                    select
                    size="small"
                    variant="outlined"
                    fullWidth
                    label={t('Language')}
                    value={form.payerLanguage}
                    onChange={(event) =>
                      set({ payerLanguage: event.target.value as 'de' | 'en' })
                    }
                  >
                    <MenuItem value="de">Deutsch</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                  </TextField>
                </div>
              )}
              <TextField
                id={fieldId('channel')}
                select
                size="small"
                variant="outlined"
                fullWidth
                label={t('Send via')}
                value={form.channel}
                onChange={(event) =>
                  set({ channel: event.target.value as FormState['channel'] })
                }
              >
                <MenuItem value="none">
                  {t('Only create (send later)')}
                </MenuItem>
                <MenuItem value="email">{t('Email')}</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
              </TextField>
              <p className="payment-request-hint">
                {form.channel === 'none'
                  ? t('You can copy and send the link after creation.')
                  : recipient ||
                    t(
                      'No contact details available. Choose another person or send the link later.'
                    )}
              </p>
              {form.channel !== 'none' && (
                <Field
                  id={fieldId('message')}
                  label={t('Message to the payer (optional)')}
                  value={form.message}
                  multiline
                  onChange={(message) => set({ message })}
                />
              )}
            </section>
            <section
              className="payment-request-create__section"
              aria-labelledby={fieldId('payment-heading')}
            >
              <h3 id={fieldId('payment-heading')}>{t('Payment')}</h3>
              <fieldset className="payment-request-kind">
                <legend>{t('Type')}</legend>
                <div className="payment-request-kind__choices">
                  {(['deposit', 'payment'] as const).map((kind) => (
                    <label key={kind}>
                      <input
                        type="radio"
                        name={fieldId('kind')}
                        value={kind}
                        checked={form.kind === kind}
                        onChange={() => set({ kind })}
                      />
                      <span>
                        {kind === 'deposit' ? t('Deposit') : t('Payment')}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="payment-request-hint">
                  {form.kind === 'deposit'
                    ? t('Credited on the final bill.')
                    : t('For example, a gift for the guest.')}
                </p>
              </fieldset>
              <Field
                id={fieldId('purpose')}
                label={t('Purpose')}
                value={form.purpose}
                error={errors.purpose}
                onChange={(purpose) => set({ purpose })}
              />
              <TextField
                id={fieldId('amount-mode')}
                select
                size="small"
                variant="outlined"
                fullWidth
                label={t('Amount entry')}
                value={form.mode}
                onChange={(event) =>
                  set({ mode: event.target.value as FormState['mode'] })
                }
              >
                <MenuItem value="amount">{t('Total amount')}</MenuItem>
                <MenuItem value="lines">{t('Invoice positions')}</MenuItem>
              </TextField>
              {form.mode === 'amount' ? (
                <div className="payment-request-create__amount">
                  <Field
                    inputMode="decimal"
                    id={fieldId('gross-amount')}
                    label={t('Gross amount (€)')}
                    value={form.amount}
                    error={errors.amount}
                    onChange={(amount) => set({ amount })}
                  />
                  <VatField
                    value={form.vatRate}
                    error={!!errors.vatRate}
                    onChange={(vatRate) => set({ vatRate })}
                  />
                </div>
              ) : (
                <LineEditor
                  lines={form.lines}
                  errors={errors}
                  onChange={(lines) => set({ lines })}
                />
              )}
              <Field
                id={fieldId('valid-until')}
                label={t('Valid until (optional)')}
                type="datetime-local"
                value={form.expiresAt}
                error={errors.expiresAt}
                helperText={t(
                  'Default: 24 hours, at most until the end of the reservation'
                )}
                onChange={(expiresAt) => set({ expiresAt })}
              />
            </section>
          </div>
        </div>
      ) : (
        <div
          ref={summaryRef}
          tabIndex={-1}
          aria-label={t('Check summary')}
          className="payment-request-form"
          style={{ display: 'grid', gap: 12 }}
        >
          <div className="payment-request-summary-heading">
            <h3>{form.purpose}</h3>
            <p className="payment-request-hint">
              {form.kind === 'deposit'
                ? t('Deposit (credited on the final bill)')
                : t('Payment (e.g. a gift)')}
            </p>
          </div>
          <ul className="payment-request-summary-lines">
            {result.ok &&
              result.input.lines.map((line, index) => (
                <li key={index}>
                  <div>
                    <strong>{line.description}</strong>
                    <span>
                      {line.quantity} × {euro(line.unitPriceCents)} ·{' '}
                      {line.vatRate} % {t('VAT')}
                    </span>
                  </div>
                  <strong>{euro(line.quantity * line.unitPriceCents)}</strong>
                </li>
              ))}
          </ul>
          <dl className="payment-request-summary-details">
            <dt>{t('Visit')}</dt>
            <dd>
              {calendarDate(reservation.date)} · {reservation.time} ·{' '}
              {guestCountLabel}
            </dd>
            <dt>{t('Payer')}</dt>
            <dd>
              {payerName}
              {form.payerSource === 'other' && form.payerCompany.trim() && (
                <small>{form.payerCompany.trim()}</small>
              )}
              {form.payerSource === 'other' && (
                <small>{t('not the reservation guest')}</small>
              )}
            </dd>
            <dt>{t('Send via')}</dt>
            <dd>
              {form.channel === 'none'
                ? t('Only create (send later)')
                : `${form.channel === 'email' ? t('Email') : 'SMS'} → ${
                    recipient || '–'
                  }`}
            </dd>
            <dt>{t('Valid until')}</dt>
            <dd>
              {dateTime(
                form.expiresAt
                  ? Date.parse(form.expiresAt)
                  : defaultExpiryPreview(reservation as any, Date.now())
              )}
            </dd>
            {form.message.trim() && (
              <>
                <dt>{t('Message to the payer (optional)')}</dt>
                <dd className="payment-request-summary-message">
                  {form.message}
                </dd>
              </>
            )}
          </dl>
          <p className="payment-request-hint">
            {t('The link is created first; sending is recorded separately.')}
          </p>
          {error && (
            <Typography color="error" block translation={null}>
              <span role="alert">{error}</span>
            </Typography>
          )}
        </div>
      )}
    </Modal>
  );
};

export default CreatePaymentRequestModal;
